import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { getAdminAreaByIdApi } from "@/api/adminArea/adminAreaApi";
import { useGetAdminAreaHierarchy } from "@/api/adminArea/userAdminAreaAPI";
import { optimizeSelectValue } from "@/api/configuration/useConfigurationAPI";
import {
  useGetAttributeValues,
  useGetMissingAttributeValues
} from "@/api/dataset/useDatasetAPI";
import type {
  DateRange,
  DateTimeRange,
  MinMaxRange,
  TimeRange
} from "@/components/dataset/datasetAttributeFilter/types";
import { isGlobalFilterAdminAreaSelected } from "@/components/dataset/datasetExplorer/poi/utils";
import { COUNTRY_NAME } from "@/constants/dataset";
import { Timezone } from "@/constants/dateTime";
import {
  createAdminAreaOption,
  createAdminAreaTreeFilter,
  generateAttributeFilterOptions,
  getAvailAggregationTypeAdminArea,
  getOptionValues,
  handleAdminAreaChangeHelper,
  hasAdminAreaInSchema,
  isAdminAreaSpatialType,
  isAdminAreaValid,
  isDateTimeRangeValid,
  resolveAreaId
} from "@/helpers/datasetAttributeHelper";
import { useGlobalAdminAreaSync } from "@/hooks/globalAdminArea/useGlobalFilterAdminArea";
import { useExploreDatasetMocks } from "@/hooks/onboarding/explorer/useExploreDatasetMocks";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { RootState } from "@/store";
import { selectIsOnboardingActive } from "@/store/slice/onboardingSlice";
import {
  ADMIN_AREA_LEVEL_MAP,
  type AdminAreaLevelKey
} from "@/types/adminArea";
import {
  type AttributeOption,
  type Dataset,
  type DatasetDetailResponse,
  type DatasetFilter,
  EFilterOptionType,
  type FilterOption
} from "@/types/dataset";
import type { AdminAreaSettings } from "@/types/spatial";

type Props = {
  timezone: Timezone;
  selectedDataset: DatasetDetailResponse | null;
  initialDatasetFilters?: DatasetFilter[];
  isAdminTreeEnabled?: boolean;
  isGlobalFilterOverrideEnabled?: boolean;
  dataset?: Dataset;
};

export const useAttributeFilter = ({
  timezone,
  selectedDataset,
  initialDatasetFilters = [],
  isAdminTreeEnabled = false,
  isGlobalFilterOverrideEnabled = false,
  dataset
}: Props) => {
  const { isAdminAreaFilterForUserDataEnabled } = useAppFeatures();
  const isOnboarding = useSelector(selectIsOnboardingActive);
  const onboardingMocks = useExploreDatasetMocks();

  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const {
    selectedAreaIds: globalSelectedAreaIds,
    adminAreaSettings: globalAdminArea
  } = useSelector((state: RootState) => state.globalFilter);
  const { syncAdminAreaToGlobalFilter } = useGlobalAdminAreaSync();

  const isGlobalFilterAreaSelected = isGlobalFilterAdminAreaSelected({
    isGlobalFilterOverrideEnabled,
    isAdminTreeEnabled,
    globalSelectedAreaIds,
    globalAdminArea
  });

  const initialOverrideAdminArea = useMemo(() => {
    if (!isGlobalFilterOverrideEnabled || !dataset) return false;
    return isGlobalFilterAreaSelected
      ? !(dataset?.is_global_admin_area ?? false)
      : false;
  }, [dataset, isGlobalFilterAreaSelected, isGlobalFilterOverrideEnabled]);

  const [selectedFilter, setSelectedFilter] = useState<AttributeOption | null>(
    null
  );
  const [selectedEditFilter, setSelectedEditFilter] =
    useState<AttributeOption | null>(null);
  const [minMaxRange, setMinMaxRange] =
    useState<DatasetFilter["minMaxRange"]>();
  const [dateTimeRange, setDateTimeRange] =
    useState<DatasetFilter["dateTimeRange"]>();
  const [dateRange, setDateRange] = useState<DatasetFilter["dateRange"]>();
  const [timeRange, setTimeRange] = useState<DatasetFilter["timeRange"]>();
  const [isEditMode, setIsEditMode] = useState(false);
  // -1 means no filter is currently being edited; >= 0 is the index in savedFilters.
  // useGetAttributeValues uses this to apply only filters before the edit position.
  const [editFilterIndex, setEditFilterIndex] = useState<number>(-1);
  const [defaultSelectedValue, setDefaultSelectedValue] =
    useState<AttributeOption | null>(null);
  const [filterOptions, setFilterOptions] = useState<AttributeOption[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedAdminArea, setSelectedAdminArea] =
    useState<AdminAreaSettings | null>(null);
  const [showErrorPOI, setShowErrorPOI] = useState(false);
  const [showAdmAreaTree, setShowAdmAreaTree] = useState(false);
  const [isOverrideAdminArea, setIsOverrideAdminArea] = useState(
    initialOverrideAdminArea
  );
  const [savedFilters, setSavedFilters] = useState<DatasetFilter[]>(
    initialDatasetFilters
  );

  // Determine filter types
  const [
    isMultipleSelection,
    isDateTimeFilter,
    isDateFilter,
    isTimeFilter,
    isMinMaxFilter
  ] = useMemo(
    () => [
      selectedFilter?.type === EFilterOptionType.MULTIPLE,
      selectedFilter?.type === EFilterOptionType.DATE_TIME,
      selectedFilter?.type === EFilterOptionType.DATE,
      selectedFilter?.type === EFilterOptionType.TIME,
      selectedFilter?.type === EFilterOptionType.MIN_MAX
    ],
    [selectedFilter]
  );

  // Fetch attribute values
  const { data: attributeValues = [], isLoading: isGettingAttributeValues } =
    useGetAttributeValues(
      {
        savedFilters,
        timezone,
        datasetId: selectedDataset?.id,
        selectedFilter,
        selectedEditFilter,
        isMultipleSelection,
        isEditMode,
        showAnalysisResultPopup,
        latestAnalysisId,
        editFilterIndex
      },
      {
        meta: {
          isOnboarding,
          onboardingMockData: onboardingMocks.attributeValues
        }
      }
    );

  // Get selected options from the filter being edited
  const editFilterSelectedOptions = useMemo(() => {
    if (!isEditMode || !selectedEditFilter) return [];

    const editingFilter = savedFilters.find(
      (f) =>
        f.filterId === selectedEditFilter.id &&
        f.attributeCode === selectedEditFilter.attribute_code
    );

    return editingFilter?.selectedOptions ?? [];
  }, [isEditMode, selectedEditFilter, savedFilters]);

  // Calculate invalid options by comparing selectedOptions with attributeValues.
  // These are options that were selected but no longer appear in the filtered results,
  // e.g. because another filter now excludes them.
  // We wait for isGettingAttributeValues=false before computing: there is no way
  // to know which options are "missing" until the filtered query has resolved.
  const editFilterInvalidOptions = useMemo(() => {
    if (!isEditMode || !selectedEditFilter || isGettingAttributeValues) {
      return [];
    }

    const availableValues = new Set(attributeValues.map((av) => av.value));

    // Find selectedOptions that don't exist in attributeValues
    return editFilterSelectedOptions.filter(
      (option) => !availableValues.has(option)
    );
  }, [
    isEditMode,
    selectedEditFilter,
    isGettingAttributeValues,
    attributeValues,
    editFilterSelectedOptions
  ]);

  // Fetch missing attribute values when editing a filter with invalid options
  const {
    data: missingAttributeValues = [],
    isLoading: isLoadingMissingValues
  } = useGetMissingAttributeValues({
    datasetId: selectedDataset?.id,
    selectedFilter: selectedEditFilter,
    invalidOptions: editFilterInvalidOptions,
    showAnalysisResultPopup,
    latestAnalysisId
  });

  // Combine attribute values with missing (stale) values.
  // Missing values are prepended so they remain visible for the user to remove.
  // Deduplication is done by value string to prevent double-entries when the
  // same value appears in both the filtered and unfiltered responses.
  const combinedAttributeValues = useMemo(() => {
    if (!missingAttributeValues.length) {
      return attributeValues;
    }

    // Exclude undefined values defensively — FilterOption.value is optional
    const existingValues = new Set(
      attributeValues.map((av) => av.value).filter(Boolean)
    );

    // Only prepend missing values that are not already present in attributeValues
    const newValues = missingAttributeValues.filter(
      (mv) => mv.value && !existingValues.has(mv.value)
    );

    // Put missing/invalid values first, then regular values
    return [...newValues, ...attributeValues];
  }, [attributeValues, missingAttributeValues]);

  // Combined loading state
  const isGettingCombinedAttributeValues =
    isGettingAttributeValues ||
    (editFilterInvalidOptions.length > 0 && isLoadingMissingValues);

  const getFilterType = (): DatasetFilter["filterType"] => {
    if (isMultipleSelection) return EFilterOptionType.MULTIPLE;
    if (isDateTimeFilter) return EFilterOptionType.DATE_TIME;
    if (isDateFilter) return EFilterOptionType.DATE;
    if (isTimeFilter) return EFilterOptionType.TIME;
    return EFilterOptionType.MIN_MAX;
  };

  const resetSelectedFilter = () => {
    setIsEditMode(false);
    setEditFilterIndex(-1);
    setSelectedFilter(null);
    setSelectedOptions([]);
    setDefaultSelectedValue(null);
    setSelectedEditFilter(null);
  };

  const resetAttributeFilter = () => {
    setDateTimeRange(undefined);
    setMinMaxRange(undefined);
    setDateRange(undefined);
    setTimeRange(undefined);
    setSearchTerm("");
  };

  const handleAddAttributeFilter = () => {
    setShowFilters(true);
    resetSelectedFilter();
    resetAttributeFilter();
    setShowErrorPOI(false);
  };

  const handleBack = () => {
    setShowFilters(false);
    setShowAdmAreaTree(false);
    resetSelectedFilter();
    resetAttributeFilter();
  };

  const lv0Option = useMemo(() => {
    const selectedCountry = selectedDataset?.country_id;
    const isValidCountry = selectedCountry && selectedCountry !== "global";
    return isValidCountry
      ? { label: COUNTRY_NAME[selectedCountry], value: selectedCountry }
      : null;
  }, [selectedDataset]);

  const [asyncAdminAreaSettings, setAsyncAdminAreaSettings] = useState<
    Partial<AdminAreaSettings>
  >({});

  useEffect(() => {
    // handle data multiple in Single UI
    const adminAreaFilter = savedFilters.find((f) =>
      f.attributeCode?.includes("adm_area_level")
    );

    if (
      !adminAreaFilter?.selectedOptions ||
      isAdminTreeEnabled ||
      !adminAreaFilter.is_multiple_adm_area
    ) {
      setAsyncAdminAreaSettings({});
      return;
    }

    const firstAdmAreaId = adminAreaFilter.selectedOptions[0]?.split("::")[0];

    if (!firstAdmAreaId) return;

    const fetchAdminArea = async () => {
      try {
        const adminAreaDetail = await getAdminAreaByIdApi(firstAdmAreaId);
        setAsyncAdminAreaSettings(optimizeSelectValue(adminAreaDetail));
      } catch {
        setAsyncAdminAreaSettings({});
      }
    };

    fetchAdminArea();
  }, [savedFilters, isAdminTreeEnabled]);

  const initialAdminArea: AdminAreaSettings = useMemo(() => {
    const adminAreaFilter = savedFilters.find((f) =>
      f.attributeCode?.includes("adm_area_level")
    );

    const adminAreaSettings: Partial<AdminAreaSettings> = {};

    if (adminAreaFilter?.selectedOptions) {
      const levelKeys: AdminAreaLevelKey[] = ["lv1", "lv2", "lv3", "lv4"];
      if (isAdminTreeEnabled) {
        // handle single and multiple data in Tree UI
        adminAreaSettings.lv0 = lv0Option
          ? { ...lv0Option, isMatched: false }
          : null;
      } else if (!adminAreaFilter.is_multiple_adm_area && !isAdminTreeEnabled) {
        // handle data single in single UI
        levelKeys.forEach((levelKey, index) => {
          const attributeCode = ADMIN_AREA_LEVEL_MAP[levelKey];
          const schema = filterOptions.find(
            (opt) => opt.attribute_code === attributeCode
          );

          if (schema) {
            // Find the corresponding selected option for the current level
            const selectedOptString = adminAreaFilter.selectedOptions?.[index];
            if (selectedOptString) {
              adminAreaSettings[levelKey] =
                createAdminAreaOption(selectedOptString);
            }
          }
        });
      }
    }

    return {
      lv0: lv0Option ? { ...lv0Option, isMatched: false } : null,
      ...adminAreaSettings,
      ...asyncAdminAreaSettings
    };
  }, [
    savedFilters,
    lv0Option,
    asyncAdminAreaSettings,
    isAdminTreeEnabled,
    filterOptions
  ]);

  const {
    data: adminAreaHierarchyData,
    isLoading: isLoadingAdminAreaHierarchy
  } = useGetAdminAreaHierarchy(initialAdminArea?.lv0?.value ?? "", {
    enabled:
      isAdminTreeEnabled &&
      !!initialAdminArea?.lv0?.value &&
      // Fetch hierarchy for user data too when the flag is on — needed to
      // browse the admin tree and resolve saved filter codes on load.
      (selectedDataset?.source !== "user" ||
        isAdminAreaFilterForUserDataEnabled)
  });

  const initialAdminAreaIds = useMemo(() => {
    if (!isAdminTreeEnabled) return [];
    const ids = new Set<string>();
    const hierarchy = adminAreaHierarchyData?.data ?? [];

    initialDatasetFilters.forEach((filter) => {
      if (!filter.attributeCode?.includes("adm_area_level")) return;

      if (filter.is_multiple_adm_area) {
        filter.selectedOptions?.forEach((option) => {
          const areaId = resolveAreaId(option, hierarchy) ?? option;
          ids.add(areaId);
        });
      } else {
        const lastOption =
          filter.selectedOptions?.[filter.selectedOptions.length - 1];
        if (lastOption) {
          const areaId = resolveAreaId(lastOption, hierarchy) ?? lastOption;
          ids.add(areaId);
        }
      }
    });

    return [...ids];
  }, [initialDatasetFilters, isAdminTreeEnabled, adminAreaHierarchyData]);

  const [selectedAdminAreaIds, setSelectedAdminAreaIds] =
    useState<string[]>(initialAdminAreaIds);

  const adminAreaValid = useMemo(() => {
    if (selectedAdminAreaIds.length > 0) return true;

    return isAdminAreaValid(
      selectedAdminArea || initialAdminArea,
      selectedDataset
    );
  }, [
    initialAdminArea,
    selectedAdminArea,
    selectedAdminAreaIds,
    selectedDataset
  ]);

  // BVT data, plus — behind the flag — user datasets that carry ADM area
  // values. User data stays optional (no mandatory validation).
  const isAdminAreaSelection =
    isAdminAreaSpatialType(selectedDataset?.spatial_aggregation_type) &&
    (selectedDataset?.source === "bvt" ||
      (isAdminAreaFilterForUserDataEnabled &&
        selectedDataset?.source === "user" &&
        hasAdminAreaInSchema(selectedDataset)));

  const isAdminAreaSelectionValid = useMemo(
    // When on admin area selection, it will check adminAreaValid
    // When not on admin area selection, it will return true
    () => (isAdminAreaSelection ? adminAreaValid : true),
    [isAdminAreaSelection, adminAreaValid]
  );

  const getAvailableFilterOptions = useCallback(
    () =>
      getAvailAggregationTypeAdminArea(
        filterOptions,
        savedFilters,
        selectedDataset
      ),
    [filterOptions, savedFilters, selectedDataset]
  );

  const isAttributeFilterValid = useMemo(() => {
    if (isMultipleSelection) return selectedOptions.length !== 0;
    if (isDateTimeFilter) return isDateTimeRangeValid(dateTimeRange);
    if (isDateFilter) return !!dateRange?.from || !!dateRange?.to;
    if (isTimeFilter) return !!timeRange?.from || !!timeRange?.to;
    if (isMinMaxFilter) return !!minMaxRange?.min || !!minMaxRange?.max;
    return false;
  }, [
    isMultipleSelection,
    selectedOptions,
    isDateTimeFilter,
    dateTimeRange,
    isDateFilter,
    dateRange,
    isTimeFilter,
    timeRange,
    isMinMaxFilter,
    minMaxRange
  ]);

  const isAdminAreaTreeSelectionValid = useMemo(() => {
    if (!showAdmAreaTree) return true;
    return selectedAdminAreaIds.length > 0;
  }, [showAdmAreaTree, selectedAdminAreaIds]);

  const handleEditFilter = (filter: DatasetFilter) => {
    const filterOption = filterOptions.find(
      (opt) =>
        opt.id === filter.filterId &&
        opt.attribute_code === filter.attributeCode
    );

    // Find the index of the filter being edited in savedFilters.
    // filterIndex may be -1 if the filter schema exists in filterOptions but the
    // filter has not been added to savedFilters yet. When -1, useGetAttributeValues
    // skips index-based filtering and falls back to the standard "remove current
    // filter" behavior, which is safe.
    const filterIndex = savedFilters.findIndex(
      (f) =>
        f.filterId === filter.filterId &&
        f.attributeCode === filter.attributeCode
    );

    if (filterOption) {
      setSelectedEditFilter(filterOption);
      setSelectedFilter(filterOption);
      setShowFilters(true);
      setIsEditMode(true);
      setEditFilterIndex(filterIndex);
      setDefaultSelectedValue(filterOption);
      setShowErrorPOI(false);
    }

    if (filter.dateTimeRange) {
      setDateTimeRange(filter.dateTimeRange);
    } else if (filter.dateRange) {
      setDateRange(filter.dateRange);
    } else if (filter.timeRange) {
      setTimeRange(filter.timeRange);
    } else if (filter.minMaxRange) {
      setMinMaxRange(filter.minMaxRange);
    } else {
      setSelectedOptions(filter.selectedOptions || []);
    }
  };

  const handleDeleteFilter = (filter: DatasetFilter) => {
    const newFilter = savedFilters.filter(
      (f) =>
        f.filterId !== filter.filterId ||
        f.attributeCode !== filter.attributeCode
    );
    setSavedFilters(newFilter);
    return newFilter;
  };

  const handleUpdateFilter = (updatedFilter: DatasetFilter) => {
    setSavedFilters((prev) =>
      prev.map((f) =>
        f.filterId === updatedFilter.filterId &&
        f.attributeCode === updatedFilter.attributeCode
          ? updatedFilter
          : f
      )
    );
  };

  const selectAttributeItem = useMemo(
    () =>
      getAvailableFilterOptions().find(
        (item) =>
          item.id === selectedFilter?.id &&
          item.attribute_code === selectedFilter?.attribute_code
      ),
    [selectedFilter, getAvailableFilterOptions]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectOption = (optionId: string) => {
    setSelectedOptions((prev) => {
      if (prev.includes(optionId)) {
        return prev.filter((id) => id !== optionId);
      }
      return [...prev, optionId];
    });
  };

  // Toggle only the search-matched values; keep hidden selections intact.
  const handleSelectAll = (data: FilterOption[]) => {
    const visibleValues = getOptionValues(data ?? []);
    const visibleValueSet = new Set(visibleValues);
    const selectedOptionsSet = new Set(selectedOptions);

    const allVisibleSelected =
      visibleValues.length > 0 &&
      visibleValues.every((value) => selectedOptionsSet.has(value));

    setSelectedOptions((prev) =>
      allVisibleSelected
        ? prev.filter((value) => !visibleValueSet.has(value))
        : Array.from(new Set([...prev, ...visibleValues]))
    );
  };

  const handleSelectFilter = (option: AttributeOption | null) => {
    if (!option || option?.disabled) return;
    setSelectedFilter(option);
    setDateTimeRange(undefined);
    setSelectedOptions([]);
    setSearchTerm("");
    setDateRange(undefined);
    setTimeRange(undefined);
    setMinMaxRange(undefined);
  };

  const handleAdminAreaChange = (newAdminArea: AdminAreaSettings | null) => {
    const newFilter = handleAdminAreaChangeHelper(
      newAdminArea,
      filterOptions,
      savedFilters
    );

    // Auto-select the first available filter option that is not disabled
    const firstAvailableOption = getAvailableFilterOptions().find(
      (option) => !option.disabled
    );

    setSavedFilters(newFilter);

    if (firstAvailableOption && !firstAvailableOption.disabled) {
      setDefaultSelectedValue(firstAvailableOption);
    } else {
      setSelectedFilter(null);
      setDefaultSelectedValue(null);
    }

    // Reset attribute filter states
    resetAttributeFilter();
  };

  const handleSaveFilter = async () => {
    const selectedFilterOption = filterOptions.find(
      (opt) =>
        opt.id === selectedFilter?.id &&
        opt.attribute_code === selectedFilter?.attribute_code
    );
    if (!selectedFilterOption) return;
    const {
      id,
      label,
      value,
      attribute_code,
      time_format,
      paired_key_attribute_id,
      data_type
    } = selectedFilterOption;
    const filterType = getFilterType();
    const newFilter: DatasetFilter = {
      filterId: id,
      filterLabel: label,
      attributeCode: attribute_code,
      attributeId: value,
      filterType: filterType,
      time_format: time_format ?? null,
      paired_key_attribute_id: paired_key_attribute_id ?? null,
      dataset_id: selectedDataset?.id ?? "",
      data_type: data_type,
      ...(isMultipleSelection ? { selectedOptions } : {}),
      ...(isDateTimeFilter && dateTimeRange ? { dateTimeRange } : {}),
      ...(isDateFilter && dateRange ? { dateRange } : {}),
      ...(isTimeFilter && timeRange ? { timeRange } : {}),
      ...(isMinMaxFilter && minMaxRange ? { minMaxRange } : {})
    };

    const updatedFilters = (prev: DatasetFilter[]) => {
      if (isEditMode) {
        return prev.map((filter) =>
          filter.filterId === selectedEditFilter?.id &&
          filter.attributeCode === selectedEditFilter?.attribute_code
            ? newFilter
            : filter
        );
      }
      return [...prev, newFilter];
    };

    if (selectedFilter) setSavedFilters(updatedFilters);

    handleBack();
    return updatedFilters(savedFilters);
  };

  const handleSaveAdminAreaTreeSelection = (
    overrideAdminAreaIds?: string[]
  ) => {
    if (!adminAreaHierarchyData) return;

    const otherFilters = savedFilters.filter(
      (f: DatasetFilter) => !f.attributeCode?.includes("adm_area_level")
    );
    const adminFilter = createAdminAreaTreeFilter(
      overrideAdminAreaIds || selectedAdminAreaIds,
      adminAreaHierarchyData.data,
      filterOptions
    );

    if (!adminFilter) return;

    setSavedFilters([adminFilter, ...otherFilters]);
    handleBack();

    return [adminFilter];
  };

  const onFilterChange = (
    data:
      | FilterOption[]
      | DateTimeRange
      | DateRange
      | TimeRange
      | MinMaxRange
      | string
  ) => {
    if (isMultipleSelection) {
      if (typeof data === "string") {
        handleSelectOption(data);
      } else if (typeof data === "object" && Array.isArray(data)) {
        handleSelectAll(data);
      }
    } else if (isDateTimeFilter) {
      setDateTimeRange(data as DateTimeRange);
    } else if (isDateFilter) {
      setDateRange(data as DateRange);
    } else if (isTimeFilter) {
      setTimeRange(data as TimeRange);
    } else if (isMinMaxFilter) {
      setMinMaxRange(data as MinMaxRange);
    }
  };

  useEffect(() => {
    setFilterOptions(generateAttributeFilterOptions(selectedDataset));
  }, [selectedDataset]);

  const resetAdminAreaIds = () => {
    setSelectedAdminAreaIds(
      savedFilters
        .find((f) => f.attributeCode === "adm_area_level_1")
        ?.selectedOptions?.map((option) => option.split("::")[0]) || []
    );
  };

  const isAdminAreaIdsSameAsGlobal = useMemo(() => {
    if (globalSelectedAreaIds.length !== selectedAdminAreaIds.length) {
      return false;
    }
    const globalIdsSet = new Set(globalSelectedAreaIds);
    return selectedAdminAreaIds.every((id) => globalIdsSet.has(id));
  }, [globalSelectedAreaIds, selectedAdminAreaIds]);

  const isFilterMode = showFilters && !showAdmAreaTree;
  const isAdminTreeMode = showAdmAreaTree && isAdminTreeEnabled;
  const isDetailMode = !showFilters && !showAdmAreaTree;
  const isUsingGlobalAdminAreaIds =
    isAdminAreaIdsSameAsGlobal && !isOverrideAdminArea;

  const groupByAttributeOptions = useMemo(() => {
    if (!filterOptions || filterOptions.length === 0) return [];

    return filterOptions.filter((option) => option?.group_by_attr);
  }, [filterOptions]);

  const isValidValueMultipleAdminAreaSameAsGlobal = useMemo(() => {
    const filterSelectionValueAdminArea = savedFilters.find((f) =>
      f.attributeCode?.includes("adm_area_level")
    )?.selectedOptions;

    if (isAdminTreeEnabled) {
      const selectedIds = filterSelectionValueAdminArea?.map(
        (option) => option.split("::")[0]
      );
      if (!selectedIds) return false;
      const globalIdsSet = new Set(globalSelectedAreaIds);
      return selectedIds.every((id) => globalIdsSet.has(id));
    } else {
      const selectedAreaId = filterSelectionValueAdminArea?.[0]?.split("::")[0];
      return (
        selectedAreaId !== undefined &&
        globalSelectedAreaIds.includes(selectedAreaId)
      );
    }
  }, [isAdminTreeEnabled, globalSelectedAreaIds, savedFilters]);

  const isValidValueSingleAdminAreaSameAsGlobal = (
    newAdminArea: AdminAreaSettings | null
  ) => {
    if (!newAdminArea?.lv1 || !globalAdminArea?.lv1) return false;
    return (
      newAdminArea?.lv1?.value === globalAdminArea?.lv1?.value &&
      newAdminArea?.lv2?.value === globalAdminArea?.lv2?.value &&
      newAdminArea?.lv3?.value === globalAdminArea?.lv3?.value &&
      newAdminArea?.lv4?.value === globalAdminArea?.lv4?.value
    );
  };

  const handleSyncSelectionGlobalAdminArea = () => {
    setSelectedAdminArea(globalAdminArea);
    setSelectedAdminAreaIds(globalSelectedAreaIds);
    if (filterOptions) {
      if (isAdminTreeEnabled) {
        handleSaveAdminAreaTreeSelection(globalSelectedAreaIds);
      } else {
        handleAdminAreaChange(globalAdminArea);
      }
    }
  };

  const getDataFilterAdminArea = async () => {
    await syncAdminAreaToGlobalFilter({
      adminArea: selectedAdminArea || initialAdminArea,
      adminAreaIds: selectedAdminAreaIds,
      isAdminTreeEnabled: isAdminTreeEnabled
    });
  };

  const onSwitchCustomArea = (checked: boolean) => {
    setIsOverrideAdminArea(checked);
    if (!checked) {
      if (isGlobalFilterAreaSelected) {
        handleSyncSelectionGlobalAdminArea();
      }
    }
  };

  const shouldLinkToGlobalFilter = (shouldApplyGlobal = false) => {
    const isAdminAreaMatchingGlobal = isAdminTreeEnabled
      ? isValidValueMultipleAdminAreaSameAsGlobal
      : isValidValueSingleAdminAreaSameAsGlobal(
          selectedAdminArea || initialAdminArea
        );
    const isShouldLinkToGlobalFilter =
      isGlobalFilterAreaSelected || isAdminAreaMatchingGlobal;
    return (
      (isShouldLinkToGlobalFilter || shouldApplyGlobal) && !isOverrideAdminArea
    );
  };

  return {
    attributeValues: combinedAttributeValues,
    isGettingAttributeValues: isGettingCombinedAttributeValues,
    selectedFilter,
    selectedEditFilter,
    defaultSelectedValue,
    filterOptions,
    savedFilters,
    setSavedFilters,
    showFilters,
    setShowFilters,
    getAvailableFilterOptions,
    initialAdminArea,
    isAttributeFilterValid,
    selectAttributeItem,
    searchTerm,
    selectedOptions,
    handleBack,
    handleAddAttributeFilter,
    handleSaveFilter,
    handleEditFilter,
    handleDeleteFilter,
    handleUpdateFilter,
    handleSearchChange,
    handleSelectFilter,
    handleAdminAreaChange,
    onFilterChange,
    selectedAdminArea,
    setSelectedAdminArea,
    adminAreaValid,
    isAdminAreaSelection,
    isAdminAreaSelectionValid,
    showErrorPOI,
    setShowErrorPOI,
    showAdmAreaTree,
    setShowAdmAreaTree,
    selectedAdminAreaIds,
    setSelectedAdminAreaIds,
    handleSaveAdminAreaTreeSelection,
    isAdminAreaTreeSelectionValid,
    initialAdminAreaIds,
    isLoadingAdminAreaHierarchy,
    resetAdminAreaIds,
    isOverrideAdminArea,
    setIsOverrideAdminArea,
    isAdminAreaIdsSameAsGlobal,
    isFilterMode,
    isAdminTreeMode,
    isDetailMode,
    isUsingGlobalAdminAreaIds,
    groupByAttributeOptions,
    isGlobalFilterAdminAreaSelected: isGlobalFilterAreaSelected,
    isValidValueMultipleAdminAreaSameAsGlobal,
    isValidValueSingleAdminAreaSameAsGlobal,
    handleSyncSelectionGlobalAdminArea,
    getDataFilterAdminArea,
    onSwitchCustomArea,
    shouldLinkToGlobalFilter,
    isEditMode,
    editFilterIndex
  };
};
